import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';

export default function Login() {
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const sessionExpired = params.get('session') === 'expired';
    const [tab, setTab] = useState('login');
    const [showPwd, setShowPwd] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [registerData, setRegisterData] = useState({ email: '', password: '', full_name: '', company_name: '' });
    const [acceptedLegal, setAcceptedLegal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Toast a friendly notice when the user was redirected here by a 401.
    useEffect(() => {
        if (sessionExpired) {
            toast.error(t('auth.session_expired_message'), { duration: 6000 });
        }
    }, [sessionExpired, t]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(loginData.email, loginData.password);
            toast.success(t('common.welcome'));
            // After re-auth, restore the user's last URL if we captured one.
            let returnTo = null;
            try { returnTo = sessionStorage.getItem('cantiereai_return_to'); } catch { /* ignore */ }
            if (returnTo) {
                sessionStorage.removeItem('cantiereai_return_to');
            }
            if (!user.onboarding_complete) navigate('/onboarding');
            else navigate(returnTo || '/app/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore login');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!acceptedLegal) {
            toast.error(t('auth.must_accept'));
            return;
        }
        setLoading(true);
        try {
            await register(registerData);
            // Log legal acceptance for GDPR Art. 7 (after token is set by register)
            try {
                const { api } = await import('../lib/api');
                const consentId = JSON.parse(localStorage.getItem('cantiereai_cookie_consent') || '{}').consent_id;
                await api.post('/consent/legal', {
                    accepted_privacy: true,
                    accepted_terms: true,
                    policy_version: '1.0.0',
                    consent_id: consentId,
                });
            } catch (ce) { /* non-blocking */ }
            toast.success(t('auth.account_created'));
            navigate('/onboarding');
        } catch (err) {
            toast.error(err.response?.data?.detail || t('auth.signup_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[hsl(220_13%_8%)] text-white">
            {/* Top bar: back arrow (left) · logo (center) · language (right) */}
            <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-white/5">
                <Link
                    to="/"
                    aria-label={t('common.back_to_site', { defaultValue: 'Torna al sito' })}
                    className="inline-flex items-center justify-center h-10 w-10 rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
                    data-testid="login-back-link"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1 flex justify-center min-w-0">
                    <Logo variant="light" />
                </div>
                <div className="flex-shrink-0">
                    <LanguageSwitcher variant="dark" compact />
                </div>
            </header>
            <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-8">
                <Card className="w-full max-w-md bg-[hsl(220_13%_10%)] border-[hsl(220_10%_22%)] text-white p-8">
                    <h1 className="font-display text-4xl mb-1">{t('auth.login_title')}</h1>
                    <p className="text-sm text-white/60 mb-6">{t('auth.signup_title')}</p>
                    
                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className="grid grid-cols-2 w-full bg-[hsl(220_10%_16%)] mb-5">
                            <TabsTrigger value="login" data-testid="login-tab" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[hsl(220_13%_10%)]">{t('auth.tab_login')}</TabsTrigger>
                            <TabsTrigger value="register" data-testid="register-tab" className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-[hsl(220_13%_10%)]">{t('auth.tab_signup')}</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="login">
                            <form onSubmit={handleLogin} className="space-y-4">
                                <div>
                                    <Label className="text-white/90">{t('auth.email')}</Label>
                                    <Input type="email" required value={loginData.email} onChange={(e) => setLoginData({...loginData, email: e.target.value})} className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1" data-testid="login-email-input" />
                                </div>
                                <div>
                                    <Label className="text-white/90">{t('auth.password')}</Label>
                                    <div className="relative mt-1">
                                        <Input type={showPwd ? 'text' : 'password'} required value={loginData.password} onChange={(e) => setLoginData({...loginData, password: e.target.value})} className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 pr-10" data-testid="login-password-input" />
                                        <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60">
                                            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11" data-testid="login-submit-button">
                                    {loading ? t('auth.signing_in') : t('auth.login_button')}
                                </Button>
                                <div className="text-right -mt-1">
                                    <Link
                                        to="/forgot-password"
                                        className="text-xs text-white/55 hover:text-[hsl(var(--accent))] hover:underline transition-colors"
                                        data-testid="forgot-password-link"
                                    >
                                        {t('auth.forgot_password_link')}
                                    </Link>
                                </div>
                            </form>
                            <div className="mt-6 pt-5 border-t border-[hsl(220_10%_22%)] text-center">
                                <p className="text-xs text-white/55 mb-2">{t('auth.no_account_yet')}</p>
                                <button
                                    type="button"
                                    onClick={() => setTab('register')}
                                    className="text-sm font-bold text-[hsl(var(--accent))] hover:text-[hsl(45_100%_60%)] transition-colors inline-flex items-center gap-1"
                                    data-testid="trial-signup-link"
                                >
                                    {t('auth.try_free_link')} <span aria-hidden="true">→</span>
                                </button>
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="register">
                            <form onSubmit={handleRegister} className="space-y-4">
                                <div>
                                    <Label className="text-white/90">{t('auth.full_name')}</Label>
                                    <Input required value={registerData.full_name} onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})} className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1" data-testid="register-fullname-input" />
                                </div>
                                <div>
                                    <Label className="text-white/90">{t('auth.company_name')}</Label>
                                    <Input value={registerData.company_name} onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})} className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1" data-testid="register-company-input" />
                                </div>
                                <div>
                                    <Label className="text-white/90">{t('auth.email')}</Label>
                                    <Input type="email" required value={registerData.email} onChange={(e) => setRegisterData({...registerData, email: e.target.value})} className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1" data-testid="register-email-input" />
                                </div>
                                <div>
                                    <Label className="text-white/90">{t('auth.password_min')}</Label>
                                    <Input type="password" required minLength={6} value={registerData.password} onChange={(e) => setRegisterData({...registerData, password: e.target.value})} className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1" data-testid="register-password-input" />
                                </div>
                                <label className="flex items-start gap-2 text-xs text-white/85 leading-snug pt-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        required
                                        checked={acceptedLegal}
                                        onChange={(e) => setAcceptedLegal(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-[hsl(var(--accent))] flex-shrink-0"
                                        data-testid="register-legal-checkbox"
                                    />
                                    <span>
                                        <Trans
                                            i18nKey="auth.legal_consent"
                                            components={[
                                                <Link key="0" to="/privacy" target="_blank" className="text-[hsl(var(--accent))] underline" />,
                                                <Link key="1" to="/terms" target="_blank" className="text-[hsl(var(--accent))] underline" />,
                                            ]}
                                        />
                                    </span>
                                </label>
                                <Button type="submit" disabled={loading || !acceptedLegal} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11 disabled:opacity-50" data-testid="register-submit-button">
                                    {loading ? t('auth.creating') : t('auth.signup_button')}
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                    
                    <div className="mt-6 text-center text-xs text-white/60">
                        <Link to="/" className="hover:text-white">← {t('common.home')}</Link>
                    </div>
                </Card>
            </div>
        </div>
    );
}
